"use client"

import { useState } from "react"
import "./Time.css"
import Button from "../components/shared/Button"

const algorithms = [
  {
    name: "Bubble Sort",
    complexity: "O(n²)",
    calculate: (n) => n * n,
    info: "Bubble Sort has a quadratic time complexity, meaning it performs poorly on large datasets due to nested loops comparing elements.",
    category: "sorting",
  },
  {
    name: "Merge Sort",
    complexity: "O(n log n)",
    calculate: (n) => n * Math.log2(n),
    info: "Merge Sort divides the array, sorts each part, and merges them back together efficiently.",
    category: "sorting",
  },
  {
    name: "Insertion Sort",
    complexity: "O(n²)",
    calculate: (n) => n * n,
    info: "Insertion Sort builds the sorted list one element at a time, performing well for small or nearly sorted datasets.",
    category: "sorting",
  },
  {
    name: "Selection Sort",
    complexity: "O(n²)",
    calculate: (n) => n * n,
    info: "Selection Sort repeatedly selects the smallest element and swaps it, making it inefficient for large datasets.",
    category: "sorting",
  },
  {
    name: "Quick Sort",
    complexity: "O(n log n)",
    calculate: (n) => n * Math.log2(n),
    info: "Quick Sort uses a pivot to partition elements, leading to efficient sorting for large datasets.",
    category: "sorting",
  },
  {
    name: "Radix Sort",
    complexity: "O(nk)",
    calculate: (n) => n * 10,
    info: "Radix Sort sorts numbers by individual digit places, making it highly efficient for certain datasets.",
    category: "sorting",
  },
  {
    name: "Bucket Sort",
    complexity: "O(n+k)",
    calculate: (n) => n + 10,
    info: "Bucket Sort distributes elements into buckets, sorting them individually before merging.",
    category: "sorting",
  },
  {
    name: "Heap Sort",
    complexity: "O(n log n)",
    calculate: (n) => n * Math.log2(n),
    info: "Heap Sort leverages a binary heap to maintain a sorted region efficiently.",
    category: "sorting",
  },
  {
    name: "AVL Tree Operations",
    complexity: "O(log n)",
    calculate: (n) => Math.log2(n),
    info: "AVL Trees keep operations logarithmic by balancing themselves after insertions or deletions.",
    category: "data-structure",
  },
  {
    name: "Tree Traversal (In-order)",
    complexity: "O(n)",
    calculate: (n) => n,
    info: "In-order traversal visits nodes in a binary tree in ascending order.",
    category: "data-structure",
  },
  {
    name: "BFS (Shortest Path)",
    complexity: "O(V + E)",
    calculate: (n) => n + (n * (n - 1)) / 2,
    info: "Breadth-First Search explores graph nodes layer by layer, useful for shortest paths in unweighted graphs.",
    category: "graph",
  },
  {
    name: "DFS (Shortest Path)",
    complexity: "O(V + E)",
    calculate: (n) => n + (n * (n - 1)) / 2,
    info: "Depth-First Search explores as far as possible before backtracking, useful for cycle detection in graphs.",
    category: "graph",
  },
  // New algorithms
  {
    name: "Traveling Salesman Problem",
    complexity: "O(n!)",
    calculate: (n) => {
      if (n > 12) return Number.POSITIVE_INFINITY
      let result = 1
      for (let i = 2; i <= n; i++) result *= i
      return result
    },
    info: "TSP finds the shortest possible route visiting each city exactly once. It has factorial complexity, making it impractical for large datasets.",
    category: "graph",
  },
  {
    name: "Red-Black Tree Operations",
    complexity: "O(log n)",
    calculate: (n) => Math.log2(n),
    info: "Red-Black Trees are self-balancing binary search trees with efficient insertion, deletion, and lookup operations.",
    category: "data-structure",
  },
  {
    name: "Stack Operations",
    complexity: "O(1)",
    calculate: (n) => 1,
    info: "Stack operations (push, pop) have constant time complexity, making them extremely efficient regardless of size.",
    category: "data-structure",
  },
  {
    name: "Queue Operations",
    complexity: "O(1)",
    calculate: (n) => 1,
    info: "Queue operations (enqueue, dequeue) have constant time complexity when implemented with linked lists.",
    category: "data-structure",
  },
  {
    name: "Linked List Operations",
    complexity: "O(n)",
    calculate: (n) => n,
    info: "Linked List operations like search and deletion have linear time complexity as they may require traversing the entire list.",
    category: "data-structure",
  },
  {
    name: "Naive String Matching",
    complexity: "O(n*m)",
    calculate: (n) => n * Math.min(n, 10),
    info: "Naive string matching compares each character of the pattern with the text, resulting in quadratic worst-case performance.",
    category: "string",
  },
  {
    name: "Rabin-Karp Algorithm",
    complexity: "O(n+m)",
    calculate: (n) => n + Math.min(n, 10),
    info: "Rabin-Karp uses hashing to find patterns in text, achieving linear average-case time complexity.",
    category: "string",
  },
  {
    name: "Longest Common Subsequence",
    complexity: "O(n*m)",
    calculate: (n) => n * Math.min(n, 10),
    info: "LCS finds the longest subsequence common to two sequences, using dynamic programming with quadratic complexity.",
    category: "dynamic-programming",
  },
  {
    name: "Boyer-Moore Algorithm",
    complexity: "O(n/m)",
    calculate: (n) => n / Math.min(Math.max(2, n / 10), 10),
    info: "Boyer-Moore is an efficient string searching algorithm that skips sections of text, often achieving sublinear time complexity.",
    category: "string",
  },
]

const categories = [
  { value: "all", label: "All Algorithms" },
  { value: "sorting", label: "Sorting Algorithms" },
  { value: "data-structure", label: "Data Structures" },
  { value: "graph", label: "Graph Algorithms" },
  { value: "string", label: "String Algorithms" },
  { value: "dynamic-programming", label: "Dynamic Programming" },
]

export default function Time() {
  const [inputSize, setInputSize] = useState(10)
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedCards, setExpandedCards] = useState({})
  const [viewMode, setViewMode] = useState("grid")
  const [sortBy, setSortBy] = useState("name")
  const [tooltipInfo, setTooltipInfo] = useState({
    visible: false,
    x: 0,
    y: 0,
    algorithm: null,
  })

  const toggleCardExpansion = (name) => {
    setExpandedCards((prev) => ({
      ...prev,
      [name]: !prev[name],
    }))
  }

  const filteredAlgorithms = algorithms
    .filter(
      (algo) =>
        (selectedCategory === "all" || algo.category === selectedCategory) &&
        algo.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortBy === "name") {
        return a.name.localeCompare(b.name)
      } else {
        return a.calculate(inputSize) - b.calculate(inputSize)
      }
    })

  // Calculate max operations for visualization
  const maxOperations = Math.max(
    ...filteredAlgorithms.map((algo) => {
      const ops = algo.calculate(inputSize)
      return isFinite(ops) ? ops : 0
    }),
  )

  const showTooltip = (e, algorithm) => {
    setTooltipInfo({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      algorithm,
    })
  }

  const hideTooltip = () => {
    setTooltipInfo({
      ...tooltipInfo,
      visible: false,
    })
  }

  return (
    <div style={{ background: '#0d1b2a', minHeight: '100vh', width: '100%' }}>
    <div className="algorithm-visualizer">
      <h1>Algorithm Complexity Visualizer</h1>

      <div className="control-panel">
        <div className="control-row">
          <div className="control-group">
            <label htmlFor="input-size">Input Size: {inputSize}</label>
            <div className="slider-container">
              <input
                type="range"
                id="input-size"
                min="1"
                max="100"
                value={inputSize}
                onChange={(e) => setInputSize(Number(e.target.value))}
                className="slider"
              />
              <input
                type="number"
                value={inputSize}
                onChange={(e) => setInputSize(Number(e.target.value))}
                min="1"
                max="100"
                className="number-input"
              />
            </div>
          </div>

          <div className="control-group">
            <label>Filter by Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="select-input"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Search Algorithms</label>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tsearch-input"
              />
              <span className="search-icon"></span>
            </div>
          </div>
        </div>

        <div className="view-controls">
          <div className="view-buttons">
            <Button
              variant=""
              className={`view-button ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
            >
              <span className="icon">📊</span> Grid View
            </Button>
            <Button
              variant=""
              className={`view-button ${viewMode === "chart" ? "active" : ""}`}
              onClick={() => setViewMode("chart")}
            >
              <span className="icon">📈</span> Chart View
            </Button>
          </div>

          <div className="sort-control">
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="select-input">
              <option value="name">Sort by Name</option>
              <option value="operations">Sort by Operations</option>
            </select>
          </div>
        </div>
      </div>

      {viewMode === "grid" ? (
        <div className="algorithm-grid">
          {filteredAlgorithms.map((algo, index) => {
            const operations = algo.calculate(inputSize)
            const isExpanded = expandedCards[algo.name] || false
            const percentage = isFinite(operations) ? (operations / maxOperations) * 100 : 100

            return (
              <div key={index} className={`algorithm-card ${isExpanded ? "expanded" : ""}`}>
                <div className="card-header">
                  <h3>{algo.name}</h3>
                  <span className="complexity-badge">{algo.complexity}</span>
                </div>

                <div className="operations-container">
                  <div className="operations-label">
                    <span>Operations:</span>
                    <span className="operations-value">{isFinite(operations) ? operations.toLocaleString() : "∞"}</span>
                  </div>
                  <div className="progress-bar-container">
                    <div className="progress-bar" style={{ width: `${Math.min(percentage, 100)}%` }}></div>
                  </div>
                </div>

                <div className={`algorithm-info ${isExpanded ? "expanded" : ""}`}>
                  <p>{algo.info}</p>
                </div>

                <Button variant="" className="expand-button" onClick={() => toggleCardExpansion(algo.name)}>
                  {isExpanded ? "Show Less ▲" : "Show More ▼"}
                </Button>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="chart-container">
          {filteredAlgorithms.map((algo, index) => {
            const operations = algo.calculate(inputSize)
            const percentage = isFinite(operations) ? (operations / maxOperations) * 100 : 100

            return (
              <div
                key={index}
                className="chart-row"
                onMouseEnter={(e) => showTooltip(e, algo)}
                onMouseLeave={hideTooltip}
              >
                <div className="chart-label">{algo.name}</div>
                <div className="chart-bar-container">
                  <div className="chart-bar" style={{ width: `${Math.min(percentage, 100)}%` }}>
                    {isFinite(operations) && percentage > 10 ? operations.toLocaleString() : ""}
                  </div>
                </div>
              </div>
            )
          })}

          {tooltipInfo.visible && tooltipInfo.algorithm && (
            <div
              className="tooltip"
              style={{
                top: `${tooltipInfo.y + 10}px`,
                left: `${tooltipInfo.x + 10}px`,
              }}
            >
              <p className="tooltip-title">{tooltipInfo.algorithm.name}</p>
              <p>Complexity: {tooltipInfo.algorithm.complexity}</p>
              <p>
                Operations:{" "}
                {isFinite(tooltipInfo.algorithm.calculate(inputSize))
                  ? tooltipInfo.algorithm.calculate(inputSize).toLocaleString()
                  : "∞"}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="info-footer">
        <p>Adjust the input size to see how different algorithms scale with larger datasets.</p>
        <p>
          <span className="info-icon">ℹ️</span>
          Some algorithms like TSP may show "∞" for large inputs due to their extreme complexity.
        </p>
      </div>
    </div>
    </div>
  )
}
